import { Button, CircularProgress, DialogProps } from "@mui/material";
import React, { useState } from "react";
import { useLanguage } from "../../Language";
import PrintIcon from "@mui/icons-material/Print";
import { FormDialog } from "@tlc/ui/dialog/FormDialog";
import { useDialog } from "@tlc/ui/dialog/useDialog";
interface PrintDialogProps extends Omit<DialogProps, "open" | "onClose"> {
  dialogTitle: string;
  htmlContent: string;
  isLoading?: boolean;
  buttonText?: string;
  disabled?: boolean;
  open?: boolean;
  onClose?: () => void;
  onOpenButtonClick?: () => Promise<void> | void;
}
const PrintDialog: React.FC<PrintDialogProps> = ({
  dialogTitle,
  htmlContent,
  isLoading = false,
  buttonText,
  disabled = false,
  open: externalOpen,
  onClose: externalOnClose,
  onOpenButtonClick,
  ...dialogProps
}) => {
  const { translate } = useLanguage();
  const { open, closeDialog, openDialog } = useDialog();
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const [isPrintLoading, setIsPrintLoading] = useState<boolean>(false);
  const isOpen = externalOpen ?? open;
  const handleClose = externalOnClose ?? closeDialog;
  const handleOpenClick = async () => {
    if (onOpenButtonClick) {
      setIsPrintLoading(true);
      try {
        await onOpenButtonClick();
      } catch (err) {
        console.log("Failed to print", err);
      } finally {
        setIsPrintLoading(false);
      }
    }
    openDialog();
  };
  const handlePrint = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    const iframe = iframeRef.current;
    if (iframe?.contentWindow) {
      iframe.contentWindow.print();
    }
  };
  const iframeContent = `
  <!DOCTYPE html>
  <html>
    <head>
      <title>${dialogTitle}</title>
      <style>
        html, body { 
          font-family: Arial, sans-serif; 
          padding: 0;
          margin: 0;
          /* Hide scrollbars across browsers */
          overflow: hidden;           
          scrollbar-width: none;      /* Firefox */
          -ms-overflow-style: none;   /* IE/Edge */
        }
        /* Chrome/Safari/Opera */
        html::-webkit-scrollbar,
        body::-webkit-scrollbar { 
          display: none; 
        }
        .pagebreak { page-break-after: always; }
        h2 { display: none; }
        @media print {
          h2 { display: block; }
        }
        table {
          width: 100%;
          table-layout: auto;
        }
      </style>
    </head>
    <body>${htmlContent}</body>
  </html>
`;
  const formId = `${(buttonText || dialogTitle)
    .replace(/\s+/g, "-")
    .toLowerCase()}-print-dialog-form`;
  return (
    <React.Fragment>
      {buttonText && (
        <Button
          onClick={handleOpenClick}
          startIcon={
            isPrintLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <PrintIcon />
            )
          }
          disabled={disabled || isPrintLoading}
        >
          {buttonText}
        </Button>
      )}
      <FormDialog
        formId={formId}
        open={isOpen}
        onClose={handleClose}
        headline={dialogTitle}
        confirmButtonText={translate("print")}
        dismissButtonText={translate("close")}
        maxWidth={dialogProps.maxWidth ?? "sm"}
      >
        <form
          onSubmit={handlePrint}
          id={formId}
          style={{
            maxHeight: "60vh",
            overflowY: "auto",
            paddingRight: "4px",
          }}
        >
          {isLoading ? (
            <CircularProgress />
          ) : (
            <iframe
              ref={iframeRef}
              srcDoc={iframeContent}
              style={{
                width: "100%",
                border: "none",
                overflow: "hidden",
                display: "block",
              }}
              title={dialogTitle}
              onLoad={(e) => {
                const iframe = e.currentTarget;
                if (iframe.contentDocument) {
                  iframe.style.height =
                    iframe.contentDocument.body.scrollHeight + 40 + "px";
                }
              }}
            />
          )}
        </form>
      </FormDialog>
    </React.Fragment>
  );
};
export default PrintDialog;
